import { useState, useEffect, useMemo, type FormEvent } from "react";
import type { Lead, Priority } from "@/mocks/leadsData";
import { fetchApi } from "@/services/apiService";

export type PipelineDeal = {
  leadId: number;
  stage: string;
  value: string;
  priority: Priority;
  assigned: string;
  closeDate: string;
  notes: string;
};

type Props = {
  preSelectedStage?: string;
  existingDealLeadIds: number[];
  onClose: () => void;
  onSave: (deal: PipelineDeal) => void;
};

const defaultStages: string[] = [
  "New Inquiry",
  "Site Visit Scheduled",
  "Site Visit Done",
  "Proposal Sent",
  "Negotiation",
  "Booking Done",
  "Lost",
];

const priorityOptions: Priority[] = ["High", "Medium", "Low"];
const today = new Date().toISOString().split("T")[0];

export default function AddDealModal({ preSelectedStage, existingDealLeadIds, onClose, onSave }: Props) {
  const [leadSearch, setLeadSearch] = useState("");
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [stage, setStage] = useState<string>(preSelectedStage || "New Inquiry");
  const [value, setValue] = useState("");
  const [priority, setPriority] = useState<Priority>("Medium");
  const [assigned, setAssigned] = useState<string>("");
  const [closeDate, setCloseDate] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [duplicateWarning, setDuplicateWarning] = useState<Lead | null>(null);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);

  const [dbLeads, setDbLeads] = useState<Lead[]>([]);
  const [dbStages, setDbStages] = useState<string[]>(defaultStages);
  const [dbAgents, setDbAgents] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 1. Fetch leads
    fetchApi<{ items?: Lead[] }>('/leads?limit=100', undefined, { items: [] })
      .then((res) => {
        if (res?.items && Array.isArray(res.items)) {
          setDbLeads(res.items);
        }
      })
      .catch((e) => console.error("Error loading leads for AddDealModal:", e));

    // 2. Fetch stages
    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) {
            setDbStages(names);
            if (!preSelectedStage) setStage(names[0]);
          }
        }
      })
      .catch(() => {});

    // 3. Fetch company users/agents
    fetchApi<any>('/auth/company-user/all', undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const agentNames: string[] = [];
        userList.forEach((t: any) => {
          if (t.name) agentNames.push(t.name);
          else if (t.fullName) agentNames.push(t.fullName);
          else if (t.username) agentNames.push(t.username);
          if (t.leader_name) agentNames.push(t.leader_name);
          if (Array.isArray(t.member_names)) agentNames.push(...t.member_names);
        });

        const unique = Array.from(new Set(agentNames)).filter(Boolean);
        if (unique.length > 0) {
          setDbAgents(unique);
          setAssigned(unique[0]);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [preSelectedStage]);

  // If preSelectedStage changes, update stage
  useEffect(() => {
    if (preSelectedStage) {
      setStage(preSelectedStage);
    }
  }, [preSelectedStage]);

  const activeAgents = dbAgents.length > 0 ? dbAgents : ["Rajesh Kumar", "Priya Sharma", "Amit Patel", "Sneha Rao"];

  // Filter leads by search
  const filteredLeads = useMemo(() => {
    if (!leadSearch.trim()) return dbLeads.slice(0, 10);
    const q = leadSearch.toLowerCase();
    return dbLeads.filter(
      (l) =>
        (l.name && l.name.toLowerCase().includes(q)) ||
        (l.company && l.company.toLowerCase().includes(q)) ||
        (l.email && l.email.toLowerCase().includes(q)) ||
        (l.phone && l.phone.includes(q))
    );
  }, [leadSearch, dbLeads]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const getInitials = (name: string | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";
  };

  const handleLeadSelect = (lead: Lead) => {
    setSelectedLead(lead);
    setLeadSearch("");
    setShowLeadDropdown(false);

    if (lead.value && lead.value !== "₹0") {
      setValue(lead.value.replace(/[₹,]/g, ""));
    }
    if (lead.priority) {
      setPriority(lead.priority as Priority);
    }
    if (lead.assigned) {
      setAssigned(lead.assigned);
    }

    // Check for duplicate in pipeline
    if (existingDealLeadIds.includes(lead.id)) {
      setDuplicateWarning(lead);
    } else {
      setDuplicateWarning(null);
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next.lead;
      return next;
    });
  };

  const handleRemoveLead = () => {
    setSelectedLead(null);
    setDuplicateWarning(null);
    setShowDuplicateConfirm(false);
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!selectedLead) e.lead = "Please select a lead";
    if (!stage) e.stage = "Please select a stage";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    if (duplicateWarning && !showDuplicateConfirm) {
      setShowDuplicateConfirm(true);
      return;
    }

    const formattedValue = value.trim()
      ? value.trim().startsWith("₹")
        ? value.trim()
        : `₹${Number(value.replace(/[^0-9.]/g, "")).toLocaleString()}`
      : "₹0";

    onSave({
      leadId: selectedLead!.id,
      stage,
      value: formattedValue,
      priority,
      assigned: assigned || activeAgents[0],
      closeDate: closeDate || today,
      notes: notes.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Deal to Pipeline</h2>
            <p className="text-xs text-gray-500 mt-0.5">Select a lead and configure deal value, stage, and owner</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Duplicate warning banner */}
        {duplicateWarning && !showDuplicateConfirm && (
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
            <i className="ri-error-warning-line text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-800">
                <strong>{duplicateWarning.name}</strong> is currently at <strong>{duplicateWarning.stage}</strong> stage. Moving will update the deal.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="text-xs font-semibold text-amber-800 hover:underline cursor-pointer"
                >
                  Proceed Anyway
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDuplicateWarning(null);
                    handleRemoveLead();
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  Choose Another
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Lead — searchable dropdown */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Select Lead <span className="text-red-500">*</span>
            </label>
            {selectedLead ? (
              <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3.5 py-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-indigo-200 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0">
                    {getInitials(selectedLead.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{selectedLead.name}</p>
                    <p className="text-xs text-gray-500 truncate">{selectedLead.company || selectedLead.phone}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveLead}
                  className="p-1 rounded-md hover:bg-indigo-200 text-gray-400 hover:text-gray-700 transition-colors"
                  title="Remove lead selection"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={leadSearch}
                  onChange={(e) => {
                    setLeadSearch(e.target.value);
                    setShowLeadDropdown(true);
                  }}
                  onFocus={() => setShowLeadDropdown(true)}
                  placeholder="Search leads by name, company, email or phone..."
                  className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${
                    errors.lead ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
                  }`}
                />
                {errors.lead && <p className="text-xs text-red-600 mt-1">{errors.lead}</p>}

                {showLeadDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowLeadDropdown(false)} />
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-52 overflow-y-auto">
                      {filteredLeads.length === 0 ? (
                        <div className="p-4 text-center text-xs text-gray-500">
                          No leads found matching &quot;{leadSearch}&quot;
                        </div>
                      ) : (
                        filteredLeads.map((lead) => (
                          <button
                            key={lead.id}
                            type="button"
                            onClick={() => handleLeadSelect(lead)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50/50 transition-colors text-left border-b border-gray-50 last:border-0 cursor-pointer"
                          >
                            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0">
                              {getInitials(lead.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 truncate">{lead.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {lead.company ? `${lead.company} • ` : ""}{lead.phone || lead.email || "No contact info"}
                              </p>
                            </div>
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                              {lead.stage || "New"}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Stage */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Stage <span className="text-red-500">*</span>
              </label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-gray-900 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${
                  errors.stage ? "border-red-300 bg-red-50" : "border-gray-200"
                }`}
              >
                {dbStages.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {errors.stage && <p className="text-xs text-red-600 mt-1">{errors.stage}</p>}
            </div>

            {/* Deal Value */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Deal Value (₹)
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">₹</span>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="e.g. 50000"
                  className="w-full pl-8 pr-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Priority */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Priority
              </label>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                {priorityOptions.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      priority === p
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                    } ${p !== priorityOptions[0] ? "border-l border-gray-200" : ""}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Assigned Agent
              </label>
              <select
                value={assigned}
                onChange={(e) => setAssigned(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              >
                {activeAgents.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Expected Close Date */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Expected Close Date
            </label>
            <input
              type="date"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Deal Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any additional context about deal requirements, budget, etc..."
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all resize-none"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 pt-3 border-t border-gray-100 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-semibold text-white transition-colors cursor-pointer whitespace-nowrap shadow-sm hover:shadow"
            >
              Add to Pipeline
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}